import { useState, useCallback } from 'react';
import { EscalationUtils } from '../types/escalation';
import borisApiService from '../services/BorisApiService';

/**
 * Custom hook for managing escalation state and operations
 * Handles escalation modal display, form submission, and success states
 */
export const useEscalation = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [escalationSchema, setEscalationSchema] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState('');
  const [successTicket, setSuccessTicket] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  /**
   * Show escalation form modal
   * @param {Object} schema - Escalation form schema
   * @param {string} sessionId - Current session ID
   */
  const showEscalationForm = useCallback((schema, sessionId) => {
    setEscalationSchema(schema);
    setCurrentSessionId(sessionId);
    setIsModalOpen(true);
    setErrors({});
  }, []);

  /**
   * Close escalation modal
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEscalationSchema(null);
    setErrors({});
  }, []);

  /**
   * Clear all errors
   */
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  /**
   * Validate form data against current schema
   * @param {Object} formData - Form data to validate
   * @returns {boolean} - Whether form is valid
   */
  const validateForm = useCallback((formData) => {
    if (!escalationSchema) return false;

    const validation = EscalationUtils.validateForm(formData, escalationSchema);
    setErrors(validation.errors);
    return validation.isValid;
  }, [escalationSchema]);

  /**
   * Clear saved form state
   */
  const clearFormState = useCallback(() => {
    try {
      sessionStorage.removeItem('escalation_draft');
    } catch (error) {
      console.warn('Failed to clear form state:', error);
    }
  }, []);

  /**
   * Save form state to session storage (for recovery)
   * @param {Object} formData - Form data to save
   */
  const saveFormState = useCallback((formData) => {
    try {
      sessionStorage.setItem('escalation_draft', JSON.stringify(formData));
    } catch (error) {
      console.warn('Failed to save form state:', error);
    }
  }, []);

  /**
   * Load form state from session storage
   * @returns {Object} - Saved form data or empty object
   */
  const loadFormState = useCallback(() => {
    try {
      const saved = sessionStorage.getItem('escalation_draft');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.warn('Failed to load form state:', error);
      return {};
    }
  }, []);

  /**
   * Submit escalation form
   * @param {Object} formData - Form data to submit
   * @returns {Promise<boolean>} - Success status
   */
  const submitEscalation = useCallback(async (formData) => {
    if (!validateForm(formData)) {
      return false;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Sanitize form data
      const sanitizedData = EscalationUtils.sanitizeFormData({
        ...formData,
        session_id: currentSessionId,
      });

      // Use the configured API service instead of direct fetch
      const ticket = await borisApiService.submitEscalation(sanitizedData);
      
      setSuccessTicket(ticket);
      
      // Close the escalation form modal
      setIsModalOpen(false);
      setEscalationSchema(null);
      
      // Show success modal
      setIsSuccessModalOpen(true);
      
      // Clear form state after successful submission
      clearFormState();
      
      return true;
      
    } catch (error) {
      console.error('Escalation submission error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 400) {
          const errorData = error.response.data;
          setErrors({ submit: errorData.detail || 'Validation failed' });
        } else if (error.response.status >= 500) {
          setErrors({ submit: 'Server error. Please try again later.' });
        } else {
          setErrors({ submit: 'Request failed. Please try again.' });
        }
      } else if (error.request) {
        // Network error
        setErrors({ submit: 'Network error. Please check your connection.' });
      } else {
        // Other error
        setErrors({ submit: error.message || 'Failed to submit escalation. Please try again.' });
      }
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [currentSessionId, escalationSchema, validateForm, clearFormState]);

  /**
   * Submit escalation with retry logic
   * @param {Object} formData - Form data to submit
   * @param {number} maxRetries - Maximum retry attempts
   * @returns {Promise<boolean>} - Success status
   */
  const submitWithRetry = useCallback(async (formData, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const success = await submitEscalation(formData);
        if (success) return true;
        
        // If validation failed, don't retry
        if (Object.keys(errors).length > 0 && !errors.submit) {
          return false;
        }
      } catch (error) {
        if (i === maxRetries - 1) {
          console.error('All retry attempts failed:', error);
          return false;
        }
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
      }
    }
    return false;
  }, [submitEscalation, errors]);

  /**
   * Close success modal
   */
  const closeSuccessModal = useCallback(() => {
    setIsSuccessModalOpen(false);
    setSuccessTicket(null);
  }, []);

  /**
   * Reset all escalation state
   */
  const resetEscalation = useCallback(() => {
    setIsModalOpen(false);
    setIsSuccessModalOpen(false);
    setEscalationSchema(null);
    setCurrentSessionId('');
    setSuccessTicket(null);
    setIsSubmitting(false);
    setErrors({});
  }, []);

  return {
    // State
    isModalOpen,
    isSuccessModalOpen,
    escalationSchema,
    currentSessionId,
    successTicket,
    isSubmitting,
    errors,
    
    // Actions
    showEscalationForm,
    closeModal,
    submitEscalation,
    submitWithRetry,
    closeSuccessModal,
    resetEscalation,
    
    // Utilities
    clearErrors,
    validateForm,
    saveFormState,
    loadFormState,
    clearFormState,
  };
};

export default useEscalation;
